'use client'

import { useEffect } from 'react'

/**
 * BrowserExtensionHandler - Handles DOM modifications from browser extensions
 *
 * This component prevents hydration errors caused by browser extensions like:
 * - Okta browser plugin
 * - Password managers (LastPass, 1Password, Bitwarden)
 * - Grammarly
 * - Ad blockers
 *
 * It works by:
 * 1. Detecting common extension-injected elements
 * 2. Adding suppressHydrationWarning to them
 * 3. Optionally hiding or removing problematic elements
 */
export default function BrowserExtensionHandler({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // List of known browser extension element selectors
    const extensionSelectors = [
      // Okta
      '#okta-modal',
      '#okta-loading',
      '.okta-wrapper',
      '[id^="okta-"]',
      '[class^="okta-"]',

      // Password Managers
      '[data-lastpass-icon-root]',
      '[data-1password]',
      '[data-bitwarden-watching]',
      'com-1password-button',

      // Grammarly
      'grammarly-desktop-integration',
      '[data-grammarly-shadow-root]',

      // Generic extension patterns
      '[id*="extension-"]',
      '[class*="extension-"]',
      'div[style*="position: fixed"][style*="z-index: 9999"]',
    ]

    const handleExtensionElements = () => {
      extensionSelectors.forEach((selector) => {
        try {
          const elements = document.querySelectorAll(selector)
          elements.forEach((element) => {
            // Add suppressHydrationWarning attribute
            if (element instanceof HTMLElement) {
              element.setAttribute('suppressHydrationWarning', 'true')

              // For Okta modal specifically, ensure it's hidden initially
              if (element.id === 'okta-modal' || element.id === 'okta-loading') {
                element.style.display = 'none'
              }
            }
          })
        } catch (error) {
          // Silently ignore selector errors
          console.debug('Extension element handler:', error)
        }
      })
    }

    // Run immediately
    handleExtensionElements()

    // Also run after a short delay to catch late-injected elements
    const timeoutId = setTimeout(handleExtensionElements, 100)

    // Set up a MutationObserver to handle dynamically added extension elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            // Check if this is likely an extension element
            const id = node.id || ''
            const className = node.className || ''

            if (
              id.includes('okta') ||
              id.includes('extension') ||
              className.includes('okta') ||
              className.includes('extension') ||
              node.hasAttribute('data-lastpass-icon-root') ||
              node.hasAttribute('data-1password') ||
              node.hasAttribute('data-bitwarden-watching')
            ) {
              node.setAttribute('suppressHydrationWarning', 'true')

              // Hide Okta elements initially
              if (id.includes('okta')) {
                node.style.display = 'none'
              }
            }
          }
        })
      })
    })

    // Start observing the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    // Cleanup
    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [])

  return <>{children}</>
}
